import React, { useState, FC } from 'react';
import { message } from 'antd';
import styles from './index.module.css';
import * as translations from './strings';
import { getLangKey } from 'appJS/models/Language';
import { put } from 'appJS/utils/fetch';
import snakecaseKeys from 'snakecase-keys';
import ArrowUp from '../../../../clients/sidebar/assets/images/ic-upgrade-circle.svg';
import { ClientFeedbackSettings } from './ClientFeedbackSettings';
import { SettingRow, SettingSection } from './SettingLayout';

const strings = translations[getLangKey()];

interface Project {
  id: number;
  organizationId: number;
  allowProjectSummaryEmail: boolean;
  allowTaskDoneEmail: boolean;
  guestPermission: string;
  allowGuestsChangeTaskStatus: boolean;
  allowGuestsViewCommits: boolean;
  assignGuests: boolean;
  acceptingFeedback: boolean;
  acceptingFeedbackEndDate: string;
  changeGuestDefaultColumn: boolean;
  guestDefaultColumnId: string | null;
  columns: {
    id: string;
    name: string;
    firstColumn: boolean;
  }[];
  organization: {
    acceptingFeedbackAllowed: boolean;
    feedbackDeadlineAllowed: boolean;
    id: string;
  };
  owner: {
    email: string;
  } | null;
}

type Props = {
  project: Project;
  guestsSeeGuestsAccess: boolean;
  guestsEditTaskStatusAllowed: boolean;
  billing: boolean;
  container?: HTMLDivElement;
};

enum GuestPermissionTypes {
  Permission = 'permission',
  AllowTaskDoneEmail = 'allowTaskDoneEmail',
  AllowProjectSummaryEmail = 'allowProjectSummaryEmail',
  AllowGuestsChangeTaskStatus = 'allowGuestsChangeTaskStatus',
  AllowGuestsViewCommits = 'allowGuestsViewCommits',
  AssignGuests = 'assignGuests'
}

type Loading = {
  [key in GuestPermissionTypes]: boolean;
};

const GuestPermissions: FC<Props> = ({
  project,
  guestsSeeGuestsAccess,
  guestsEditTaskStatusAllowed,
  billing,
  container
}) => {
  if (!project) return null;

  const {
    id,
    organizationId,
    guestPermission,
    allowTaskDoneEmail: _allowTaskDoneEmail,
    allowProjectSummaryEmail: _allowProjectSummaryEmail,
    allowGuestsChangeTaskStatus: _allowGuestsChangeTaskStatus,
    allowGuestsViewCommits: _allowGuestsViewCommits,
    assignGuests: _assignGuests
  } = project;

  const [loading, setLoading] = useState<Loading>({
    [GuestPermissionTypes.Permission]: false,
    [GuestPermissionTypes.AllowTaskDoneEmail]: false,
    [GuestPermissionTypes.AllowProjectSummaryEmail]: false,
    [GuestPermissionTypes.AllowGuestsChangeTaskStatus]: false,
    [GuestPermissionTypes.AllowGuestsViewCommits]: false,
    [GuestPermissionTypes.AssignGuests]: false
  });

  const [permission, setPermission] = useState(guestPermission);
  const [
    allowGuestsChangeTaskStatus,
    setAllowGuestsChangeTaskStatus
  ] = useState(_allowGuestsChangeTaskStatus ?? true);
  const [allowProjectSummaryEmail, setAllowProjectSummaryEmail] = useState(
    _allowProjectSummaryEmail ?? true
  );
  const [allowTaskDoneEmail, setAllowTaskDoneEmail] = useState(
    _allowTaskDoneEmail ?? true
  );
  const [allowGuestsViewCommits, setAllowGuestsViewCommits] = useState(
    _allowGuestsViewCommits ?? true
  );
  const [assignGuests, setAssignGuests] = useState(_assignGuests ?? true);

  const updateProject = async (
    body: {
      allowTaskDoneEmail?: boolean;
      allowProjectSummaryEmail?: boolean;
      permission?: 'guests_see_guests' | 'guests_see_self';
      allowGuestsChangeTaskStatus?: boolean;
      allowGuestsViewCommits?: boolean;
      assignGuests?: boolean;
      allowGuestsDeleteTasks?: boolean;
    },
    loadingKey: GuestPermissionTypes,
    onError
  ) => {
    setLoading(previousValue => ({ ...previousValue, [loadingKey]: true }));
    const response = await put(
      `/projects/${id}.json`,
      snakecaseKeys(body, { deep: true })
    );
    if (!response.ok) {
      onError();
      message.error(
        'Something went wrong while updating the project, please try again.'
      );
    } else {
      message.success('Project updated successfully');
    }
    setLoading(previousValue => ({ ...previousValue, [loadingKey]: false }));
  };

  const subscriptionEndpoint = `/organizations/${organizationId}/subscription`;
  const canEditGuestAccess =
    guestsSeeGuestsAccess && guestsEditTaskStatusAllowed;

  return (
    <div className={styles.container}>
      <SettingSection title={strings.guestCollaboration}>
        {!canEditGuestAccess && (
          <div
            className={styles.upgradeAlert}
            onClick={() => window.open(subscriptionEndpoint, '_self')}
          >
            <ArrowUp className={styles.upgradeIcon} />
            <span className={styles.upgradeText}>
              {strings.onlyAvailableOnPremium}
              <a href={subscriptionEndpoint}>{strings.upgradeNow}</a>
            </span>
          </div>
        )}
        <SettingRow
          checked={permission === 'guests_see_guests'}
          disabled={!canEditGuestAccess}
          loading={loading[GuestPermissionTypes.Permission]}
          label={strings.allowGuestSeeGuestsFeedback}
          helpArticleId="84854"
          onChange={checked => {
            const newPermission = checked
              ? 'guests_see_guests'
              : 'guests_see_self';
            setPermission(newPermission);
            updateProject(
              { permission: newPermission },
              GuestPermissionTypes.Permission,
              () =>
                setPermission(
                  checked ? 'guests_see_self' : 'guests_see_guests'
                )
            );
          }}
        />
        <SettingRow
          checked={allowGuestsViewCommits}
          disabled={!canEditGuestAccess}
          loading={loading[GuestPermissionTypes.AllowGuestsViewCommits]}
          label={strings.allowGuestsViewCommitContent}
          helpArticleId="85000"
          onChange={checked => {
            setAllowGuestsViewCommits(checked);
            updateProject(
              { allowGuestsViewCommits: checked },
              GuestPermissionTypes.AllowGuestsViewCommits,
              () => setAllowGuestsViewCommits(!checked)
            );
          }}
        />
        <SettingRow
          checked={allowGuestsChangeTaskStatus}
          disabled={!canEditGuestAccess}
          loading={loading[GuestPermissionTypes.AllowGuestsChangeTaskStatus]}
          label={strings.allowGuestChangeTaskStatus}
          helpArticleId="85000"
          onChange={checked => {
            setAllowGuestsChangeTaskStatus(checked);
            updateProject(
              { allowGuestsChangeTaskStatus: checked },
              GuestPermissionTypes.AllowGuestsChangeTaskStatus,
              () => setAllowGuestsChangeTaskStatus(!checked)
            );
          }}
        />
        <SettingRow
          checked={assignGuests}
          disabled={!canEditGuestAccess}
          loading={loading[GuestPermissionTypes.AssignGuests]}
          label={strings.allowGuestsAssignedToTasks}
          helpArticleId="85000"
          onChange={checked => {
            setAssignGuests(checked);
            updateProject(
              { assignGuests: checked },
              GuestPermissionTypes.AssignGuests,
              () => setAssignGuests(!checked)
            );
          }}
        />
      </SettingSection>

      <SettingSection title={strings.guestNotifications} helpArticleId="85000">
        <SettingRow
          checked={allowProjectSummaryEmail}
          loading={loading[GuestPermissionTypes.AllowProjectSummaryEmail]}
          label={strings.projectSummaryEmail}
          onChange={checked => {
            setAllowProjectSummaryEmail(checked);
            updateProject(
              { allowProjectSummaryEmail: checked },
              GuestPermissionTypes.AllowProjectSummaryEmail,
              () => setAllowProjectSummaryEmail(!checked)
            );
          }}
        />
        <SettingRow
          checked={allowTaskDoneEmail}
          loading={loading[GuestPermissionTypes.AllowTaskDoneEmail]}
          label={strings.taskDoneEmail}
          onChange={checked => {
            setAllowTaskDoneEmail(checked);
            updateProject(
              { allowTaskDoneEmail: checked },
              GuestPermissionTypes.AllowTaskDoneEmail,
              () => setAllowTaskDoneEmail(!checked)
            );
          }}
        />
      </SettingSection>

      <ClientFeedbackSettings
        project={{ ...project, id: String(project.id) }}
        billing={billing}
        container={container}
      />
    </div>
  );
};

export default GuestPermissions;
